// Copyright (c) 2025 Goffy. All Rights Reserved.
//
// Minimal OIDC PKCE helper for local dev with Keycloak.

import { Buffer } from 'buffer';
import { CryptoDigestAlgorithm, CryptoEncoding, digestStringAsync, getRandomBytesAsync } from 'expo-crypto';

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

// These must be plain `process.env.X` member expressions: babel-preset-expo only
// inlines that exact shape, so optional chaining or destructuring leaves a runtime
// lookup that resolves to undefined in a native bundle. React Native also defines
// `window` without `location`, so every fallback has to tolerate its absence.
const defaultRealmUrl =
  process.env.EXPO_PUBLIC_KEYCLOAK_BASE || 'https://backend.goffy.app/realms/goffy';

const webOrigin = typeof window !== 'undefined' ? window.location?.origin : undefined;

export const KEYCLOAK_BASE = defaultRealmUrl.replace(/\/$/, '');
export const CLIENT_ID = 'bff-public';
export const REDIRECT_URI =
  process.env.EXPO_PUBLIC_REDIRECT_URI ||
  (webOrigin ? `${webOrigin}/callback` : 'https://goffy.app/callback');
export const POST_LOGOUT_REDIRECT =
  process.env.EXPO_PUBLIC_POST_LOGOUT_REDIRECT ||
  (webOrigin ? `${webOrigin}/` : 'https://goffy.app/');

// Include offline_access so Keycloak issues a durable (offline) refresh token. Without
// it the refresh token is session-bound and dies after the SSO idle window (~30 min),
// after which returning to a protected page silently re-authenticates through the
// /callback screen — the visible "Callback" flash. A durable token lets the background
// refresh in user-context succeed instead, so no redirect/callback transit happens.
const AUTH_SCOPE = 'openid profile email offline_access';

const VERIFIER_KEY = 'goffy:pkce:verifier';
const STATE_KEY = 'goffy:pkce:state';
const getSessionStorage = () => (typeof window !== 'undefined' ? window.sessionStorage : null);
const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);
let nativeVerifier: string | null = null;
let nativeState: string | null = null;

function base64UrlFromBase64(base64: string) {
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlEncode(bytes: Uint8Array) {
  const base64 = typeof btoa === 'function'
    ? btoa(String.fromCharCode(...Array.from(bytes)))
    : Buffer.from(bytes).toString('base64');
  return base64UrlFromBase64(base64);
}

async function sha256Base64(input: string) {
  if (typeof crypto !== 'undefined' && crypto.subtle?.digest) {
    const data = new TextEncoder().encode(input);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return Buffer.from(new Uint8Array(hash)).toString('base64');
  }

  return digestStringAsync(CryptoDigestAlgorithm.SHA256, input, { encoding: CryptoEncoding.BASE64 });
}

async function randomString(length = 64) {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const arr = new Uint8Array(length);
    crypto.getRandomValues(arr);
    return base64UrlEncode(arr);
  }

  const bytes = await getRandomBytesAsync(length);
  return base64UrlEncode(bytes);
}

export async function buildAuthUrl(options?: { idpHint?: string }) {
  const verifier = await randomString(48);
  const challenge = base64UrlFromBase64(await sha256Base64(verifier));
  const state = await randomString(16);
  const sessionStorage = getSessionStorage();
  const localStorage = getLocalStorage();
  if (sessionStorage || localStorage) {
    sessionStorage?.setItem(VERIFIER_KEY, verifier);
    sessionStorage?.setItem(STATE_KEY, state);
    localStorage?.setItem(VERIFIER_KEY, verifier);
    localStorage?.setItem(STATE_KEY, state);
  } else {
    if (asyncStorage) {
      await asyncStorage.setItem(VERIFIER_KEY, verifier);
      await asyncStorage.setItem(STATE_KEY, state);
    } else {
      nativeVerifier = verifier;
      nativeState = state;
    }
  }

  const u = new URL(`${KEYCLOAK_BASE}/protocol/openid-connect/auth`);
  u.searchParams.set('client_id', CLIENT_ID);
  u.searchParams.set('response_type', 'code');
  u.searchParams.set('scope', AUTH_SCOPE);
  u.searchParams.set('redirect_uri', REDIRECT_URI);
  u.searchParams.set('code_challenge', challenge);
  u.searchParams.set('code_challenge_method', 'S256');
  u.searchParams.set('state', state);
  // kc_idp_hint sends the user straight to the named Keycloak identity provider
  // (e.g. "google") instead of the Keycloak login form.
  if (options?.idpHint) {
    u.searchParams.set('kc_idp_hint', options.idpHint);
  }
  return u.toString();
}

export function buildForgotPasswordUrl(redirectUri: string = REDIRECT_URI) {
  // Keycloak's reset-credentials endpoint starts a fresh password-reset flow
  // when given a client_id (no prior auth session required). redirect_uri sends
  // the user back to the app afterwards and must be a registered redirect for the client.
  const u = new URL(`${KEYCLOAK_BASE}/login-actions/reset-credentials`);
  u.searchParams.set('client_id', CLIENT_ID);
  u.searchParams.set('redirect_uri', redirectUri);
  return u.toString();
}

export function decodeBase64UrlUtf8(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(padded, 'base64').toString('utf-8');
  }
  if (typeof atob === 'function') {
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    if (typeof TextDecoder !== 'undefined') {
      return new TextDecoder('utf-8').decode(bytes);
    }
    return decodeURIComponent(
      Array.from(bytes)
        .map((byte) => `%${byte.toString(16).padStart(2, '0')}`)
        .join('')
    );
  }
  return padded;
}

function parseJwt(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    const json = decodeBase64UrlUtf8(payload);
    return JSON.parse(json);
  } catch {
    return {};
  }
}

export async function exchangeCode(code: string, returnedState?: string) {
  const sessionStorage = getSessionStorage();
  const localStorage = getLocalStorage();
  const asyncState = asyncStorage ? await asyncStorage.getItem(STATE_KEY) : null;
  const storedState =
    sessionStorage?.getItem(STATE_KEY) ??
    localStorage?.getItem(STATE_KEY) ??
    asyncState ??
    nativeState;
  if (!storedState || storedState !== returnedState) {
    throw new Error('Invalid state');
  }
  const asyncVerifier = asyncStorage ? await asyncStorage.getItem(VERIFIER_KEY) : null;
  const verifier =
    sessionStorage?.getItem(VERIFIER_KEY) ??
    localStorage?.getItem(VERIFIER_KEY) ??
    asyncVerifier ??
    nativeVerifier;
  if (!verifier) throw new Error('Missing PKCE verifier');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: REDIRECT_URI,
    client_id: CLIENT_ID,
    code_verifier: verifier,
  });

  const tokenEndpoint = `${KEYCLOAK_BASE}/protocol/openid-connect/token`;
  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`Token exchange failed: ${res.status}`);
  }
  const json = (await res.json()) as { access_token?: string; id_token?: string; refresh_token?: string };
  if (!json.access_token) throw new Error('No access token');

  sessionStorage?.removeItem(STATE_KEY);
  sessionStorage?.removeItem(VERIFIER_KEY);
  localStorage?.removeItem(STATE_KEY);
  localStorage?.removeItem(VERIFIER_KEY);
  if (asyncStorage) {
    await asyncStorage.removeItem(STATE_KEY);
    await asyncStorage.removeItem(VERIFIER_KEY);
  }
  nativeState = null;
  nativeVerifier = null;

  const claims = parseJwt(json.id_token ?? json.access_token);
  const sub = (claims['sub'] as string) ?? '';
  const name = (claims['name'] as string) ?? (claims['preferred_username'] as string) ?? 'User';

  return {
    accessToken: json.access_token,
    idToken: json.id_token ?? null,
    refreshToken: json.refresh_token ?? null,
    id: sub,
    name,
  };
}

export async function loginWithPassword(username: string, password: string) {
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: CLIENT_ID,
    username,
    password,
    scope: AUTH_SCOPE,
  });

  const tokenEndpoint = `${KEYCLOAK_BASE}/protocol/openid-connect/token`;
  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const errorBody = await res.text().catch(() => '');
    throw new Error(`Password login failed: ${res.status}${errorBody ? ` ${errorBody}` : ''}`);
  }

  const json = (await res.json()) as { access_token?: string; id_token?: string; refresh_token?: string };
  if (!json.access_token) throw new Error('No access token');

  const claims = parseJwt(json.id_token ?? json.access_token);
  const sub = (claims['sub'] as string) ?? '';
  const name = (claims['name'] as string) ?? (claims['preferred_username'] as string) ?? username;

  return {
    accessToken: json.access_token,
    idToken: json.id_token ?? null,
    refreshToken: json.refresh_token ?? null,
    id: sub,
    name,
  };
}

/**
 * Keycloak answered the token endpoint with an error status. A rejection that is *not* this
 * type came from the transport — offline, DNS, a proxy hiccup — and says nothing about whether
 * the refresh token is still good. Callers must keep the two apart: treating a dropped
 * connection as a dead session is what sent players back to the Keycloak login form.
 */
export class TokenRefreshError extends Error {
  readonly status: number;
  readonly oauthError: string | null;

  constructor(status: number, oauthError: string | null) {
    super(`Token refresh failed: ${status}${oauthError ? ` (${oauthError})` : ''}`);
    this.name = 'TokenRefreshError';
    this.status = status;
    this.oauthError = oauthError;
  }

  /**
   * Keycloak rejected the credential itself (expired, revoked, or already rotated — the realm
   * runs revokeRefreshToken with refreshTokenMaxReuse 0, so every refresh token is single-use).
   * 5xx and 429 are deliberately excluded: those are worth retrying.
   */
  get isSessionEnded(): boolean {
    return this.status === 400 || this.status === 401;
  }
}

export async function refreshAccessToken(refreshToken: string) {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: CLIENT_ID,
  });
  const tokenEndpoint = `${KEYCLOAK_BASE}/protocol/openid-connect/token`;
  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const raw = await res.text().catch(() => '');
    let oauthError: string | null = null;
    try {
      oauthError = (JSON.parse(raw) as { error?: string }).error ?? null;
    } catch {
      // Not every error body is JSON (a gateway can answer before Keycloak does).
    }
    throw new TokenRefreshError(res.status, oauthError);
  }
  const json = (await res.json()) as { access_token?: string; id_token?: string; refresh_token?: string };
  if (!json.access_token) throw new Error('No access token');

  return {
    accessToken: json.access_token,
    idToken: json.id_token ?? null,
    refreshToken: json.refresh_token ?? refreshToken,
  };
}

export async function revokeRefreshToken(refreshToken: string) {
  const body = new URLSearchParams({
    client_id: CLIENT_ID,
    token: refreshToken,
    token_type_hint: 'refresh_token',
  });
  const revokeEndpoint = `${KEYCLOAK_BASE}/protocol/openid-connect/revoke`;
  const res = await fetch(revokeEndpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`Token revoke failed: ${res.status}`);
  }
}

export function parseAuthCallback(url: string) {
  try {
    const parsed = new URL(url);
    return {
      code: parsed.searchParams.get('code') ?? null,
      state: parsed.searchParams.get('state') ?? null,
    };
  } catch {
    return { code: null, state: null };
  }
}

export function buildLogoutUrl(idTokenHint?: string | null, redirectUri: string = POST_LOGOUT_REDIRECT) {
  const u = new URL(`${KEYCLOAK_BASE}/protocol/openid-connect/logout`);
  u.searchParams.set('client_id', CLIENT_ID);
  u.searchParams.set('post_logout_redirect_uri', redirectUri);
  if (idTokenHint) {
    u.searchParams.set('id_token_hint', idTokenHint);
  }
  return u.toString();
}
